/**
 * OfficeLayout - Shared shell for all office pages
 *
 * Two-tier navigation with 4 centered pillars:
 *   Row 1: Home | Sales | Ops | Admin (always visible, centered)
 *   Row 2: Sub-links for the active pillar (persistent, no hover required)
 *
 * Design: Clean glassmorphism, high contrast, centered layout.
 */

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { RouteFallback } from '@/components/RouteFallback';
import { motion } from 'framer-motion';
import {
  Home,
  Sparkles,
  Compass,
  Building2,
  Shield,
  Zap,
  FolderKanban,
  Wifi,
  CalendarDays,
  AlertCircle,
  Package,
  Receipt,
  ClipboardList,
  Users,
  LogOut,
  ChevronDown,
  Settings,
  Construction,
  Menu,
  X,
  Sun,
  Moon,
  MapPin,
  CalendarCheck,
  Bug,
  MessageSquare,
  DollarSign,
  Wrench,
  Clock,
  Briefcase,
  Brain,
  Trophy,
  Truck,
  Boxes,
  ListChecks,
  Gift,
  MoreHorizontal,
  Dices,
} from 'lucide-react';
import { lazy, Suspense, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useFeatures } from '@/hooks/useFeatureGate';
import { useTerminology } from '@/hooks/useTradePack';
import type { FeatureKey } from '@shared/domain/feature-packs';
import { QuizBell } from '../tech/QuizBell';
import { MentionNotificationBell } from '../mentions/MentionNotificationBell';
import { WeeklyQuizModal } from '../WeeklyQuizModal';
import { useQuizNotification } from '@/hooks/useWeeklyQuiz';
import { getTenantIdSync } from '@/hooks/useTenantId';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { NotificationPromptBanner, NotificationToast } from '@/components/notifications/NotificationPrompt';
import { useTheme } from '@/hooks/useTheme';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { usePageTelemetry } from '@/hooks/usePageTelemetry';
import VotingButton from '../voting/VotingButton';
import { ShellSwitcher } from '@/components/ShellSwitcher';
import VotingModal from '../voting/VotingModal';
import VotingAnnouncementModal from '../voting/VotingAnnouncementModal';
import { useVotingAnnouncement } from '@/hooks/useVotingAnnouncement';
import { useDirectorAccess } from '@/hooks/useDirectorAccess';
import { useFinancialVisibility } from '@/context/FinancialVisibilityContext';
import { FieldAlertBar } from './FieldAlertBar';
import { FieldDayPill } from './FieldDayPill';
// Lazy: FieldDayModal reaches TechJobDetailV6 and most of src/components/tech.
// Statically imported it put 864 kB into the office entry chunk for a modal
// that only dual-access users ever open. The pill (always rendered) lives in
// its own module so it stays cheap.
const FieldDayModal = lazy(() =>
  import('./FieldDayModal').then((m) => ({ default: m.FieldDayModal })),
);
import { useTeam } from '@/hooks/useTeam';
import { isDualAccessFallback } from '@/data/team';
import { useVoting } from '@/hooks/useVoting';
import { recoverVotingState } from '@/services/voting.service';
import { clearAllAuth } from '@/hooks/useAuth';
import { BugReportModal } from '@/components/BugReportModal';
import { WhatsNewModal } from '@/components/whatsNew/WhatsNewModal';
import { WhatsNewAutoPopup } from '@/components/whatsNew/WhatsNewAutoPopup';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import { D20Dice } from '@/components/D20Dice';
import { GlobalSearchProvider } from '@/components/search/GlobalSearchProvider';
import { GlobalSearchTrigger } from '@/components/search/GlobalSearchTrigger';
import { CustomerProfileProvider } from '@/components/customers/CustomerProfileProvider';

// ============================================================================
// Navigation Data
// ============================================================================

interface SubLink {
  key: string;
  label: string;
  path: string;
  icon: typeof Zap;
  comingSoon?: boolean;
  /** Feature-pack gate — sub-link hidden unless the tenant's packs grant
   *  this feature (docs/FEATURE_PACKS_PLAN.md). Unset = always shown. */
  featureKey?: FeatureKey;
  /** Trade-pack terminology key. When the active pack overrides it, the
   *  PLURAL form replaces `label` — 'Faults' reads 'Callbacks' for a gate
   *  installer. Unset = the label is trade-neutral and never rewritten. */
  termKey?: string;
}

interface NavPillar {
  key: string;
  label: string;
  icon: typeof Sparkles;
  defaultPath: string;
  /** Tailwind color class for active state (e.g. 'text-brand-secondary') */
  activeColor: string;
  /** Tailwind bg for active pill */
  activeBg: string;
  /** Tailwind bg for active sub-link */
  subActiveBg: string;
  /** Tailwind border color for the active indicator */
  indicatorColor: string;
  subLinks: SubLink[];
  /** Paths that make this pillar active */
  matchPaths: string[];
}

const DIRECTORS_PILLAR: NavPillar = {
  key: 'directors',
  label: 'Directors',
  icon: Shield,
  defaultPath: '/home/directors',
  activeColor: 'text-brand-secondary',
  activeBg: 'bg-brand-secondary/10',
  subActiveBg: 'bg-brand-secondary/10',
  indicatorColor: 'bg-brand-secondary',
  matchPaths: ['/home/directors', '/home/directors/ops', '/home/directors/timesheets'],
  subLinks: [
    { key: 'directors-money', label: 'Money', path: '/home/directors', icon: DollarSign },
    { key: 'directors-ops', label: 'Ops', path: '/home/directors/ops', icon: Wrench },
    { key: 'directors-timesheets', label: 'Timesheets', path: '/home/directors/timesheets', icon: Clock },
  ],
};

const BASE_NAV_PILLARS: NavPillar[] = [
  {
    key: 'home',
    label: 'Home',
    icon: Home,
    defaultPath: '/home',
    activeColor: 'text-brand-secondary',
    activeBg: 'bg-brand-secondary/10',
    subActiveBg: 'bg-brand-secondary/10',
    indicatorColor: 'bg-brand-secondary',
    // Note: /home/plate has no nav tab of its own — the Plate page is reached
    // by EXPANDING the dashboard's "On Your Plate" widget (and via mention /
    // reminder deep-links). getActivePillar falls through to 'home' for it.
    matchPaths: ['/home'],
    subLinks: [],
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: Sparkles,
    defaultPath: '/home/deals',
    activeColor: 'text-brand-secondary',
    activeBg: 'bg-brand-secondary/10',
    subActiveBg: 'bg-brand-secondary/10',
    indicatorColor: 'bg-brand-secondary',
    matchPaths: ['/home/deals', '/home/projects'],
    subLinks: [
      { key: 'deals', label: 'Deals', path: '/home/deals', icon: Zap },
      { key: 'projects', label: 'Projects', path: '/home/projects', icon: FolderKanban, featureKey: 'projects' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: Compass,
    defaultPath: '/home/ops',
    activeColor: 'text-blue-400',
    activeBg: 'bg-blue-500/10',
    subActiveBg: 'bg-blue-500/10',
    indicatorColor: 'bg-blue-400',
    matchPaths: [
      '/home/ops',
      '/home/schedule',
      '/home/map',
      '/home/jobs',
      '/home/faults',
      '/home/service-requests',
      '/home/action-items',
      '/home/provisioning',
    ],
    subLinks: [
      { key: 'schedule', label: 'Schedule', path: '/home/schedule', icon: CalendarDays },
      { key: 'map', label: 'Map', path: '/home/map', icon: MapPin },
      { key: 'jobs', label: 'Jobs', path: '/home/jobs', icon: ClipboardList },
      { key: 'faults', label: 'Faults', path: '/home/faults', icon: AlertCircle, termKey: 'fault' },
      { key: 'service-requests', label: 'Service Requests', path: '/home/service-requests', icon: MessageSquare },
      { key: 'action-items', label: 'Action Items', path: '/home/action-items', icon: ListChecks },
      { key: 'provisioning', label: 'Provisioning', path: '/home/provisioning', icon: Package, featureKey: 'provisioning' },
    ],
  },
  {
    key: 'records',
    label: 'Records',
    icon: Building2,
    defaultPath: '/home/admin',
    activeColor: 'text-emerald-400',
    activeBg: 'bg-emerald-500/10',
    subActiveBg: 'bg-emerald-500/10',
    indicatorColor: 'bg-emerald-400',
    matchPaths: [
      '/home/admin',
      '/home/billing',
      '/home/services',
      '/home/customers',
      '/home/legacy-deals',
      '/home/leave',
      '/home/vehicles',
      '/home/parts-hub',
    ],
    subLinks: [
      { key: 'customers', label: 'Customers', path: '/home/customers', icon: Users },
      { key: 'services', label: 'Services', path: '/home/services', icon: Wifi, featureKey: 'customer-services' },
      { key: 'billing', label: 'Billing', path: '/home/billing', icon: Receipt },
      { key: 'parts-hub', label: 'Parts Hub', path: '/home/parts-hub', icon: Boxes },
      { key: 'vehicles', label: 'Vehicles', path: '/home/vehicles', icon: Truck },
      { key: 'leave', label: 'Leave', path: '/home/leave', icon: CalendarCheck },
    ],
  },
];

/** Build nav pillars, conditionally including Directors at the front.
 *  Director access resolves from tenant.ownerIds via useDirectorAccess() —
 *  reactive, so the pillar appears as soon as the tenant config loads on a
 *  cold start instead of staying hidden until a manual refresh (the legacy
 *  DIRECTOR_NAMES name-list gate was retired once ownerIds shipped). */
/** Sub-links that lead to a financial-visibility-gated page. Hidden from
 *  people who can't see money — otherwise the nav advertises a destination
 *  that only ever shows them "Financial figures hidden". */
const FINANCIAL_SUBLINK_KEYS = new Set(['billing']);

function buildNavPillars(
  hasDirectorAccess: boolean,
  canViewFinancials: boolean,
  features: Set<FeatureKey>,
  termPlural: (key: string, fallback: string) => string,
): NavPillar[] {
  const base = BASE_NAV_PILLARS.map((pillar) => ({
    ...pillar,
    subLinks: pillar.subLinks
      .filter(
        (l) =>
          (canViewFinancials || !FINANCIAL_SUBLINK_KEYS.has(l.key)) &&
          (!l.featureKey || features.has(l.featureKey)),
      )
      // Relabel AFTER filtering — `key` and `path` are untouched, so routing,
      // active-state matching and feature gating all still work off the
      // original identifiers. Only the visible string changes.
      .map((l) => (l.termKey ? { ...l, label: termPlural(l.termKey, l.label) } : l)),
  }));
  if (hasDirectorAccess) {
    return [DIRECTORS_PILLAR, ...base];
  }
  return base;
}

function getActivePillar(pathname: string, pillars: NavPillar[]): string {
  // Check non-home pillars first (more specific paths)
  for (const pillar of pillars) {
    if (pillar.key === 'home') continue;
    if (pillar.matchPaths.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
      return pillar.key;
    }
  }
  // Default to home for /home and /home/dashboard
  if (pathname === '/home' || pathname === '/home/dashboard') {
    return 'home';
  }
  return 'home';
}

function getActiveSubLink(pathname: string, pillars: NavPillar[]): string | null {
  // Prefer the longest-matching path so nested sub-routes like
  // /home/directors/ops don't get captured by /home/directors.
  let bestKey: string | null = null;
  let bestLen = -1;
  for (const pillar of pillars) {
    for (const link of pillar.subLinks) {
      if (pathname === link.path || pathname.startsWith(link.path + '/')) {
        if (link.path.length > bestLen) {
          bestLen = link.path.length;
          bestKey = link.key;
        }
      }
    }
  }
  return bestKey;
}

// ============================================================================
// Main Layout
// ============================================================================

export function OfficeLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
  const [isDiceOpen, setIsDiceOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // What's New — drives the "new" dot on the More (•••) menu + clears it on view.
  const { hasUnseen: whatsNewUnseen, markViewed: markWhatsNewViewed } = useWhatsNew();
  const { isDark, mode, setMode } = useTheme();

  // Office routes default to DARK mode unless the user has explicitly chosen
  // a preference. The preference is stored separately from the tech one so
  // dual-access users (Reuben, Guy) can have dark office + light tech.
  useEffect(() => {
    const pref = localStorage.getItem('office_theme_preference');
    const target: 'dark' | 'light' = pref === 'light' || pref === 'dark' ? pref : 'dark';
    if (mode !== target) setMode(target);
     
  }, []);

  const handleToggleTheme = useCallback(() => {
    const next: 'dark' | 'light' = isDark ? 'light' : 'dark';
    localStorage.setItem('office_theme_preference', next);
    setMode(next);
  }, [isDark, setMode]);

  // Live location broadcasting for map view
  useLocationTracking();
  // Per-tenant product analytics (page-view counters) for the platform
  // console's Usage page. Counters only; never throws.
  usePageTelemetry();

  const userName = localStorage.getItem('logged_in_user') || 'User';
  const userId = localStorage.getItem('user_id') || localStorage.getItem('tech_id') || '';
  const userRole = localStorage.getItem('user_role') || 'office';
  const initials = userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  // Build nav pillars — Directors pillar only visible to directors. Reactive:
  // useDirectorAccess() re-resolves when the tenant (ownerIds) loads, so the
  // pillar appears without a refresh on cold start.
  const { isDirector: hasDirectorAccess } = useDirectorAccess();
  // Billing sub-link hides for anyone not cleared to see money — the page
  // itself is gated, so linking to it would just advertise a locked door.
  const { canViewFinancials } = useFinancialVisibility();
  // Feature-pack gating: sub-links whose featureKey the tenant's packs don't
  // grant disappear (e.g. Services/Provisioning for a non-MSP tenant).
  const features = useFeatures();
  const { termPlural } = useTerminology();
  const navPillars = useMemo(
    () => buildNavPillars(hasDirectorAccess, canViewFinancials, features, termPlural),
    [hasDirectorAccess, canViewFinancials, features, termPlural],
  );

  const activePillarKey = getActivePillar(location.pathname, navPillars);
  const activePillar = navPillars.find((p) => p.key === activePillarKey)!;
  const activeSubLink = getActiveSubLink(location.pathname, navPillars);
  const hasSubLinks = activePillar.subLinks.length > 0;

  // Quiz notification hook
  const { showNotification: isQuizAvailable, hasCompleted: hasCompletedQuiz } = useQuizNotification(userId || null);

  // Push notifications
  const { latestNotification, dismissNotification } = usePushNotifications(userId || null);

  // Canonical tenant ID (matches login flow + Cloud Functions).
  const votingTenantId = getTenantIdSync();

  // Employee of the Month announcement — reveals once per user per period
  const votingAnnouncement = useVotingAnnouncement(userId || null, votingTenantId);

  // Self-heal the voting system on office load (fire-and-forget, once-per-load).
  // Closes any expired-but-active period the scheduled Cloud Function may have
  // missed, and backfills Hall-of-Fame stats for closed periods that never had
  // them applied (e.g. closed by the old server path before the stats fix).
  // Idempotent — see recoverVotingState().
  useEffect(() => {
    if (!userId) return;
    recoverVotingState(votingTenantId).catch((err) => {
      console.error('[voting] background recovery failed:', err);
    });
  }, [userId, votingTenantId]);

  // Voting state — used to drop the prominent VotingButton from the nav
  // once the user has voted. Period stays accessible via the dropdown,
  // which opens VotingModal directly (so users can review their vote
  // even after the in-nav button has disappeared).
  const {
    hasVoted: hasVotedThisPeriod,
    isVotingOpen: isVotingPeriodOpen,
    votingPeriod: currentVotingPeriod,
  } = useVoting(userId || null, userName, votingTenantId);
  const [isVotingModalOpenFromMenu, setIsVotingModalOpenFromMenu] = useState(false);

  // Field Day modal — for ANY dual-access user (Reuben, Guy, Brady, etc.).
  // Prefer the Firestore `dualAccess` flag, but fall back to a hardcoded name
  // list so the pill keeps working for Reuben/Guy/Brady even when their user
  // doc hasn't been migrated yet. team data loads async, so the name fallback
  // also avoids a blank-pill flash on first paint.
  const { team } = useTeam();
  const isDualAccessUser = useMemo(() => {
    if (!userId && !userName) return false;
    const flagMatch = userId
      ? team.some((m) => m.id === userId && m.dualAccess === true)
      : false;
    return flagMatch || isDualAccessFallback(userName);
  }, [team, userId, userName]);

  const [isFieldDayOpen, setIsFieldDayOpen] = useState(false);
  // Auto-open when landing from a deployment-mention link (/home?fieldDay=1)
  useEffect(() => {
    if (!isDualAccessUser) return;
    const params = new URLSearchParams(location.search);
    if (params.get('fieldDay') === '1') {
      setIsFieldDayOpen(true);
      // Clean the URL so refresh doesn't re-open
      params.delete('fieldDay');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
    }
     
  }, [location.search, isDualAccessUser]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    clearAllAuth();
    navigate('/', { replace: true });
  };

  return (
    <GlobalSearchProvider>
    <CustomerProfileProvider>
    <div className="min-h-screen bg-surface flex flex-col transition-colors duration-200">
      {/* Top Navigation — safe-top pushes below iOS status bar / Dynamic Island */}
      <nav className="sticky top-0 z-50 safe-top bg-surface-card/95 backdrop-blur-2xl border-b border-theme transition-colors duration-200">
        {/* Row 1: Mobile Hamburger (< md) + Desktop Navigation (>= md) */}
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-8">
          <div className="relative flex items-center justify-between lg:justify-center h-12 sm:h-14">
            {/* Hamburger (visible < lg — anything narrower than a small laptop
                gets the drawer, since the absolute-positioned desktop nav
                starts overlapping itself well before 1024px). */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Desktop: Global Search Pill (anchored left so tabs stay perfectly centered) */}
            <div className="hidden lg:block absolute left-0">
              <GlobalSearchTrigger variant="pill" />
            </div>

            {/* Desktop: Centered Pillar Tabs (hidden < lg, visible >= lg) */}
            <div className="hidden lg:flex items-center gap-0.5 sm:gap-1">
              {navPillars.map((pillar) => {
                const isActive = activePillarKey === pillar.key;
                const PillarIcon = pillar.icon;

                return (
                  <button
                    key={pillar.key}
                    onClick={() => navigate(pillar.defaultPath)}
                    className={`relative flex items-center gap-2 px-2.5 sm:px-4 lg:px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? `${pillar.activeColor} ${pillar.activeBg}`
                        : 'text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover'
                    }`}
                  >
                    <PillarIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{pillar.label}</span>

                    {/* Active indicator bar */}
                    {isActive && (
                      <motion.div
                        layoutId="pillarIndicator"
                        className={`absolute bottom-0 left-2 sm:left-3 right-2 sm:right-3 h-[2px] rounded-full ${pillar.indicatorColor}`}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop: User Menu (absolute right, hidden < lg, visible >= lg) */}
            <div className="hidden lg:flex absolute right-0 items-center gap-2" ref={menuRef}>
              {/* Cross to the field shell. Renders nothing unless the user is
                  entitled (Lite tenant, or an individually flagged dual-access
                  user), so a Pro tenant's office staff never see it. */}
              <ShellSwitcher current="office" compact />
              {/* More (•••) — What's New, Weekly Quiz, d20, Report a bug.
                  Consolidates the secondary utilities so the bar stays clean.
                  Shows a dot when there's an unseen update or a quiz to do. */}
              <div className="relative" ref={moreMenuRef}>
                <button
                  onClick={() => setShowMoreMenu((v) => !v)}
                  className="relative p-1.5 rounded-lg text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover transition-all"
                  title="More"
                  aria-label="More"
                  aria-expanded={showMoreMenu}
                >
                  <MoreHorizontal className="w-4 h-4" />
                  {(whatsNewUnseen || (isQuizAvailable && !hasCompletedQuiz)) && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-brand-secondary ring-2 ring-surface-card" />
                  )}
                </button>

                {showMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-1.5 w-56 bg-surface-card border border-theme rounded-xl shadow-2xl overflow-hidden z-50"
                  >
                    <div className="py-1">
                      {/* What's New */}
                      <button
                        onClick={() => { setShowMoreMenu(false); setIsWhatsNewOpen(true); }}
                        className="w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                      >
                        <span className="flex items-center gap-2.5">
                          <Gift className="w-3.5 h-3.5" />
                          What's New
                        </span>
                        {whatsNewUnseen && (
                          <span className="text-[9px] font-bold text-brand-secondary">NEW</span>
                        )}
                      </button>

                      {/* Weekly Quiz */}
                      <button
                        onClick={() => { setShowMoreMenu(false); setIsQuizModalOpen(true); }}
                        className="w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                      >
                        <span className="flex items-center gap-2.5">
                          <Brain className="w-3.5 h-3.5" />
                          Weekly Quiz
                        </span>
                        {hasCompletedQuiz ? (
                          <span className="text-[9px] font-bold text-emerald-500/80">DONE</span>
                        ) : isQuizAvailable ? (
                          <span className="text-[9px] font-bold text-brand-secondary">NEW</span>
                        ) : null}
                      </button>

                      {/* Roll a d20 — modal rendered at layout root so closing
                          this menu can't unmount it mid-roll. */}
                      <button
                        onClick={() => { setShowMoreMenu(false); setIsDiceOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                      >
                        <Dices className="w-3.5 h-3.5" />
                        Roll a d20
                      </button>

                      {/* Report a bug / feature */}
                      <button
                        onClick={() => { setShowMoreMenu(false); setIsBugReportOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                      >
                        <Bug className="w-3.5 h-3.5" />
                        Report a bug / feature
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Employee Voting — drops out of the nav once the user has
                  voted. Period stays reachable via the user dropdown. */}
              {userId && !hasVotedThisPeriod && (
                <VotingButton
                  userId={userId}
                  userName={userName}
                  tenantId={votingTenantId}
                />
              )}

              {/* Field Alert Bell (SOS, Flag, More Time from techs) */}
              <FieldAlertBar tenantId={getTenantIdSync()} />

              {/* Field Day pill — only renders when a dual-access user has
                  jobs scheduled in the next 2 days (urgency signal). The
                  permanent entry point lives in the user-avatar dropdown
                  below so the pill can stay quiet on calm days. */}
              {isDualAccessUser && userId && (
                <FieldDayPill
                  userId={userId}
                  onClick={() => setIsFieldDayOpen(true)}
                />
              )}

              {/* Notification Bell (for @mentions / tagging) */}
              {userId && (
                <MentionNotificationBell
                  userId={userId}
                  tenantId={getTenantIdSync()}
                />
              )}

              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-transparent hover:border-theme hover:bg-surface-hover transition-all duration-200"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-secondary/30 to-brand-secondary/15 border border-brand-secondary/25 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-brand-secondary">{initials}</span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-medium text-theme-primary leading-tight">{userName}</p>
                  <p className="text-[9px] text-theme-muted capitalize">{userRole}</p>
                </div>
                <ChevronDown className={`w-3 h-3 text-theme-muted transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-1.5 w-52 bg-surface-card border border-theme rounded-xl shadow-2xl overflow-hidden"
                >
                  <div className="p-3.5 border-b border-theme-soft">
                    <p className="text-xs font-semibold text-theme-primary">{userName}</p>
                    <p className="text-[10px] text-theme-muted capitalize mt-0.5">{userRole} account</p>
                  </div>
                  <div className="py-1">
                    {/* My Field Day — permanent entry for dual-access users.
                        Pill in the nav bar only renders on deployed days, so
                        this dropdown row is the always-on access point. */}
                    {isDualAccessUser && userId && (
                      <button
                        onClick={() => { setShowUserMenu(false); setIsFieldDayOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-purple-500 hover:bg-purple-500/10 transition-all"
                      >
                        <Briefcase className="w-3.5 h-3.5" />
                        My Field Day
                      </button>
                    )}

                    {/* Weekly Quiz — always reachable from here so users can
                        review results after completing. */}
                    <button
                      onClick={() => { setShowUserMenu(false); setIsQuizModalOpen(true); }}
                      className="w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                    >
                      <span className="flex items-center gap-2.5">
                        <Brain className="w-3.5 h-3.5" />
                        Weekly Quiz
                      </span>
                      {hasCompletedQuiz ? (
                        <span className="text-[9px] font-bold text-emerald-500/80">DONE</span>
                      ) : isQuizAvailable ? (
                        <span className="text-[9px] font-bold text-brand-secondary">NEW</span>
                      ) : null}
                    </button>

                    {/* Voting — only show when a period is open. After the
                        user votes, this is the way back in to view results. */}
                    {userId && isVotingPeriodOpen && (
                      <button
                        onClick={() => { setShowUserMenu(false); setIsVotingModalOpenFromMenu(true); }}
                        className="w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                      >
                        <span className="flex items-center gap-2.5">
                          <Trophy className="w-3.5 h-3.5" />
                          Vote
                        </span>
                        {hasVotedThisPeriod && (
                          <span className="text-[9px] font-bold text-emerald-500/80">VOTED</span>
                        )}
                      </button>
                    )}

                    {/* Theme toggle — moved here from the nav bar to keep the
                        bar quieter. Personal config feels at home in the
                        avatar menu. */}
                    <button
                      onClick={() => { setShowUserMenu(false); handleToggleTheme(); }}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                    >
                      {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                      {isDark ? 'Light Mode' : 'Dark Mode'}
                    </button>

                    <button
                      onClick={() => { setShowUserMenu(false); navigate('/settings/general'); }}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs text-red-400/70 hover:text-red-400 hover:bg-red-500/[0.05] transition-all"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      Sign Out
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Compact bar (visible < lg) — keeps the critical badges (SOS
                field alerts, unread @mentions) reachable at narrow widths
                instead of burying them in the drawer. */}
            <div className="lg:hidden flex items-center gap-1">
              <FieldAlertBar tenantId={getTenantIdSync()} />
              {userId && (
                <MentionNotificationBell
                  userId={userId}
                  tenantId={getTenantIdSync()}
                />
              )}
              <GlobalSearchTrigger variant="icon" />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-secondary/30 to-brand-secondary/15 border border-brand-secondary/25 flex items-center justify-center">
                <span className="text-[10px] font-bold text-brand-secondary">{initials}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Sub-links for active pillar (desktop only, always visible when pillar has sub-links) */}
        {hasSubLinks && (
          <div className="hidden lg:block border-t border-theme-soft">
            <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-8">
              <div className="flex items-center justify-center gap-0.5 sm:gap-1 h-10 overflow-x-auto scrollbar-hide">
                {activePillar.subLinks.map((link) => {
                  const LinkIcon = link.icon;
                  const isLinkActive = activeSubLink === link.key;

                  return (
                    <button
                      key={link.key}
                      onClick={() => {
                        if (!link.comingSoon) navigate(link.path);
                      }}
                      disabled={link.comingSoon}
                      className={`relative flex items-center gap-1.5 px-2 sm:px-3 lg:px-4 py-1.5 text-xs font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
                        link.comingSoon
                          ? 'text-theme-muted cursor-not-allowed'
                          : isLinkActive
                            ? `${activePillar.activeColor} ${activePillar.subActiveBg}`
                            : 'text-theme-tertiary hover:text-theme-secondary hover:bg-surface-hover'
                      }`}
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{link.label}</span>
                      {link.comingSoon && (
                        <span className="hidden sm:flex items-center gap-0.5 ml-1">
                          <Construction className="w-2.5 h-2.5" />
                          <span className="text-[9px]">Soon</span>
                        </span>
                      )}
                      {isLinkActive && (
                        <motion.div
                          layoutId="subLinkIndicator"
                          className={`absolute bottom-0 left-1 sm:left-2 right-1 sm:right-2 h-[2px] rounded-full ${activePillar.indicatorColor}`}
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Menu Drawer (slide from left) */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMenuOpen(false)}
            className="fixed inset-0 bg-surface-overlay backdrop-blur-sm z-[100] lg:hidden"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-[280px] bg-surface-card border-r border-theme z-[101] lg:hidden overflow-y-auto"
          >
            {/* Header — safe-top pushes below iOS status bar / Dynamic Island */}
            <div className="safe-top flex items-center justify-between p-4 border-b border-theme-soft">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-secondary/30 to-brand-secondary/15 border border-brand-secondary/25 flex items-center justify-center">
                  <span className="text-xs font-bold text-brand-secondary">{initials}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-theme-primary">{userName}</p>
                  <p className="text-xs text-theme-tertiary capitalize">{userRole}</p>
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation menu"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <div className="p-3 space-y-1">
              {/* Main Pillars */}
              {navPillars.map((pillar) => {
                const isActive = activePillarKey === pillar.key;
                const PillarIcon = pillar.icon;

                return (
                  <div key={pillar.key}>
                    <button
                      onClick={() => {
                        navigate(pillar.defaultPath);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? `${pillar.activeColor} ${pillar.activeBg}`
                          : 'text-theme-secondary hover:text-theme-primary hover:bg-surface-hover'
                      }`}
                    >
                      <PillarIcon className="w-5 h-5" />
                      {pillar.label}
                    </button>

                    {/* Sub-links (show when pillar is active) */}
                    {isActive && pillar.subLinks.length > 0 && (
                      <div className="ml-8 mt-1 space-y-0.5">
                        {pillar.subLinks.map((link) => {
                          const LinkIcon = link.icon;
                          const isLinkActive = activeSubLink === link.key;

                          return (
                            <button
                              key={link.key}
                              onClick={() => {
                                if (!link.comingSoon) {
                                  navigate(link.path);
                                  setIsMobileMenuOpen(false);
                                }
                              }}
                              disabled={link.comingSoon}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                                link.comingSoon
                                  ? 'text-theme-muted cursor-not-allowed'
                                  : isLinkActive
                                    ? `${pillar.activeColor} ${pillar.subActiveBg}`
                                    : 'text-theme-tertiary hover:text-theme-secondary hover:bg-surface-hover'
                              }`}
                            >
                              <LinkIcon className="w-4 h-4" />
                              {link.label}
                              {link.comingSoon && (
                                <span className="ml-auto flex items-center gap-0.5 text-theme-muted">
                                  <Construction className="w-3 h-3" />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Divider */}
            <div className="mx-3 my-2 border-t border-theme-soft" />

            {/* Action Items */}
            <div className="p-3 space-y-1">
              {/* Field Day — dual-access users only */}
              {isDualAccessUser && userId && (
                <button
                  onClick={() => {
                    setIsFieldDayOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-purple-500 hover:bg-purple-500/10 transition-all"
                >
                  <Briefcase className="w-5 h-5" />
                  <span>My Field Day</span>
                </button>
              )}

              {/* Theme Toggle */}
              <button
                onClick={handleToggleTheme}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              {/* Quiz Bell */}
              <button
                onClick={() => {
                  setIsQuizModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                <QuizBell
                  isQuizAvailable={isQuizAvailable}
                  hasCompleted={hasCompletedQuiz}
                  onClick={() => {}}
                />
                <span>Weekly Quiz</span>
              </button>

              {/* Report Bug / Feature */}
              <button
                onClick={() => {
                  setIsBugReportOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                <Bug className="w-5 h-5" />
                Report Bug / Feature
              </button>

              {/* What's New */}
              <button
                onClick={() => {
                  navigate('/home/whats-new');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                <Gift className="w-5 h-5" />
                What's New
              </button>

              {/* Settings */}
              <button
                onClick={() => {
                  navigate('/settings/general');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all"
              >
                <Settings className="w-5 h-5" />
                Settings
              </button>

              {/* Sign Out */}
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400/70 hover:text-red-400 hover:bg-red-500/[0.05] transition-all"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </motion.div>
        </>
      )}

      {/* Push notification prompt */}
      <NotificationPromptBanner userId={userId || null} />

      {/* Page Content */}
      <main className="flex-1">
        {/* Suspense sits INSIDE the shell, not around it.

        The single boundary in App.tsx wraps all of <Routes>, so any
        lazy route resolving unmounted the entire tree — nav, sidebar,
        everything — and the user watched the whole app disappear and
        come back on every navigation. That is the "flash" that arrived
        with route splitting; the chunk load was never the slow part.

        With the boundary here, only the content region swaps. The shell
        is statically imported and stays mounted, so navigation looks the
        way it did before splitting: the page changes, the furniture
        doesn't. The outer boundary still covers this layout's own chunk
        and the public routes. */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Foreground notification toast */}
      <NotificationToast
        notification={latestNotification}
        onDismiss={dismissNotification}
        onTap={(url) => navigate(url)}
      />

      {/* Weekly Quiz Modal */}
      <WeeklyQuizModal
        isOpen={isQuizModalOpen}
        onClose={() => setIsQuizModalOpen(false)}
        techId={userId}
        techName={userName}
      />

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={() => setIsBugReportOpen(false)}
        currentPage={location.pathname}
      />

      {/* What's New — opened from the More (•••) menu. Portals to <body>. */}
      <WhatsNewModal
        isOpen={isWhatsNewOpen}
        onClose={() => { setIsWhatsNewOpen(false); markWhatsNewViewed(); }}
        onViewAll={() => navigate('/home/whats-new')}
      />

      {/* d20 dice — opened from the More (•••) menu. Portals to <body>. */}
      <D20Dice isOpen={isDiceOpen} onClose={() => setIsDiceOpen(false)} />

      {/* What's New auto-popup — currently gated OFF (see WHATS_NEW_AUTOPOPUP_ENABLED
          in src/data/whatsNew.ts). This stays mounted so re-enabling is a
          one-flag flip. */}
      <WhatsNewAutoPopup enableViewAll />

      {/* Field Day Modal — mounted for any dual-access user. Cheap when closed
          (returns null inside) so always-on for this audience is fine. */}
      {isDualAccessUser && userId && isFieldDayOpen && (
        // Mounted only while OPEN, not merely when the user is entitled — the
        // chunk shouldn't be fetched until they actually ask for it. No
        // fallback: the trigger is a click, and a flash of chrome behind a
        // modal that's about to cover it is worse than nothing.
        <Suspense fallback={null}>
          <FieldDayModal
            isOpen={isFieldDayOpen}
            onClose={() => setIsFieldDayOpen(false)}
            userId={userId}
            userName={userName}
          />
        </Suspense>
      )}

      {/* Voting modal — dropdown-triggered. Lets users open the voting flow
          from the avatar menu even when the in-nav VotingButton has hidden
          itself (e.g. after they've voted, to review). */}
      {userId && (
        <VotingModal
          isOpen={isVotingModalOpenFromMenu}
          onClose={() => setIsVotingModalOpenFromMenu(false)}
          userId={userId}
          userName={userName}
          votingPeriod={currentVotingPeriod}
          hasVoted={hasVotedThisPeriod}
          tenantId={votingTenantId}
        />
      )}

      {/* Employee of the Month announcement — auto-opens once per user when results are ready */}
      <VotingAnnouncementModal
        isOpen={votingAnnouncement.isOpen}
        onClose={votingAnnouncement.dismiss}
        results={votingAnnouncement.announcement}
      />
    </div>
    </CustomerProfileProvider>
    </GlobalSearchProvider>
  );
}
